import type {
  HotelRoomTrackingProperties,
  HotelRoomRateTrackingProperties,
  HotelShopTrackingProperties,
  HotelShopAvailabilityTrackingProperties,
} from "@b2bportal/core-tracking";
import type { QuoteErrorType } from "@b2bportal/core-types";
import type {
  CheckoutEventName,
  QuotedProductTrackingProperties,
} from "../types";

export enum HotelCheckoutEventName {
  hotel_complete_buy = "hotel_complete_buy",
  hotel_review_details = "hotel_review_details",
  hotel_view_travelers = "hotel_view_travelers",
}

export type HotelCheckoutHTSEventBasicProperties =
  HotelShopAvailabilityTrackingProperties["properties"] &
    HotelRoomTrackingProperties["properties"] &
    HotelRoomRateTrackingProperties["properties"] &
    HotelShopTrackingProperties["properties"];

export type HotelCheckoutHTSEventProperties<Properties = object> =
  HotelCheckoutHTSEventBasicProperties & Properties;

export type HotelPurchaseReviewedProperties =
  HotelCheckoutHTSEventProperties<QuotedProductTrackingProperties>;

export type HotelPurchaseReviewed = {
  name: HotelCheckoutEventName.hotel_review_details;
  properties: HotelPurchaseReviewedProperties;
  encryptedProperties: string[];
};

export type HotelCheckoutBuySuccessfullyCompleted = {
  name: HotelCheckoutEventName.hotel_complete_buy;
  properties: HotelPurchaseReviewedProperties & {
    booking_id: string;
    success: true;
  };
  encryptedProperties: string[];
};

export type HotelCheckoutBuyUnsuccessfullyCompleted = {
  name: HotelCheckoutEventName.hotel_complete_buy;
  properties: HotelPurchaseReviewedProperties & {
    success: false;
  };
  encryptedProperties: string[];
};

export type HotelContactInfoEnabled = {
  name: CheckoutEventName.viewed_contact_info;
  properties: HotelCheckoutHTSEventProperties<object>;
  encryptedProperties: string[];
};

export type HotelTravelersEnabled = {
  name: HotelCheckoutEventName.hotel_view_travelers;
  properties: HotelCheckoutHTSEventProperties<object>;
  encryptedProperties: string[];
};
export type HotelPaymentSectionReviewed = {
  name: CheckoutEventName.view_payment;
  properties: HotelCheckoutHTSEventProperties<object>;
  encryptedProperties: string[];
};

export const HOTEL_PRODUCT_TYPE = "hotel" as const;
export const QUOTE_STEP = "quote" as const;

export type HotelCartQuoteErrorViewed = {
  name: CheckoutEventName.modal_alert;
  properties: {
    product_type: typeof HOTEL_PRODUCT_TYPE;
    modal_type: QuoteErrorType;
    screen: "hotels_checkout";
    step: typeof QUOTE_STEP;
  };
  encryptedProperties: string[];
};

export type GuestPromotionErrorDisplayed = {
  name: CheckoutEventName.modal_alert;
  properties: {
    product_type: typeof HOTEL_PRODUCT_TYPE;
    screen: "hotels_checkout";
    modal_type: "mfa_authentication_popup";
    step: typeof QUOTE_STEP;
  };
  encryptedProperties: string[];
};

export type HotelCheckoutHTSEvent =
  | HotelCheckoutBuySuccessfullyCompleted
  | HotelCheckoutBuyUnsuccessfullyCompleted
  | HotelContactInfoEnabled
  | HotelPurchaseReviewed
  | HotelTravelersEnabled
  | HotelPaymentSectionReviewed
  | HotelCartQuoteErrorViewed
  | GuestPromotionErrorDisplayed;
